import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { ArrowRight, MapPin, Sparkles, Truck, Wind, Tag } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import GeoMeta from "@/components/GeoMeta";
import { useAds } from "@/store/ads";
import { useCart } from "@/store/cart";
import { useHeroVariant } from "@/lib/useHeroVariant";

const KEYWORDS_WHITELIST = new Set([
  "sexy-air-fresheners",
  "erotic-air-fresheners",
  "sexy-woman-air-fresheners",
  "booty-air-fresheners",
  "sexy-car-air-fresheners",
]);

export default function GeoLanding() {
  const { keyword, slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { adMode, promoCode, attribution } = useAds();
  const { addItem, setDrawerOpen } = useCart();
  const hero = useHeroVariant(slug);

  useEffect(() => {
    setData(null); setNotFound(false);
    if (!KEYWORDS_WHITELIST.has(keyword)) {
      setNotFound(true);
      return;
    }
    api.get(`/geo/${keyword}/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [keyword, slug]);

  if (notFound || !KEYWORDS_WHITELIST.has(keyword)) {
    // Let the router handle other paths (e.g. 404). Redirect to home for safety.
    return <Navigate to="/" replace />;
  }
  if (!data) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-white/50 text-sm">Loading…</div>;
  }

  const canonicalPath = `/${keyword}/${data.slug}`;
  const cityLabel = data.state ? `${data.city}, ${data.state}` : data.city;

  // JSON-LD blocks
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bootybreeze.com/" },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": "https://bootybreeze.com/shop" },
      { "@type": "ListItem", "position": 3, "name": data.keyword_phrase, "item": `https://bootybreeze.com/${keyword}` },
      { "@type": "ListItem", "position": 4, "name": cityLabel, "item": `https://bootybreeze.com${canonicalPath}` },
    ],
  };

  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (data.faqs || []).map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const place = data.lat != null && data.lon != null ? {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": cityLabel,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": data.lat,
      "longitude": data.lon,
    },
    "address": { "@type": "PostalAddress", "addressLocality": data.city, "addressRegion": data.state, "addressCountry": "US" },
  } : null;

  const productListLd = data.listings.slice(0, 12).map((p) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "description": p.description || p.tagline,
    "image": p.image_url && p.image_url.startsWith("http") ? p.image_url : `https://bootybreeze.com${p.image_url || ""}`,
    "sku": p.id,
    "brand": { "@type": "Brand", "name": "Booty Breeze" },
    "offers": {
      "@type": "Offer",
      "url": `https://bootybreeze.com/product/${p.id}`,
      "priceCurrency": "USD",
      "price": p.price,
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "areaServed": place ? cityLabel : "United States",
    },
  }));

  const aggregate = data.listings.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": `${data.keyword_phrase} in ${cityLabel}`,
    "numberOfItems": data.listings.length,
    "itemListElement": data.listings.slice(0, 12).map((p, i) => ({
      "@type": "Offer",
      "position": i + 1,
      "url": `https://bootybreeze.com/product/${p.id}`,
      "priceCurrency": "USD",
      "price": p.price,
      "itemOffered": { "@type": "Product", "name": p.name, "brand": "Booty Breeze" },
    })),
  } : null;

  return (
    <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-12" data-testid={`geo-page-${keyword}-${data.slug}`}>
      {/* Geo-matched hero bg for city landing */}
      <div
        aria-hidden
        className="absolute inset-0 -z-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.95) 40%, #050505 100%), url(${hero.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
        data-testid={`geo-hero-bg-${hero.hero_variant}`}
      />
      <div className="relative z-10">
      <SEO
        title={data.seo_title}
        description={data.seo_description}
        path={canonicalPath}
        jsonLd={{ "@graph": [breadcrumb, faqPage, ...(place ? [place] : []), ...(aggregate ? [aggregate] : []), ...productListLd] }}
      />
      <GeoMeta city={data.city} state={data.state} lat={data.lat} lon={data.lon} />

      {/* Sticky offer bar — paid traffic only */}
      {adMode && (
        <div
          className="mb-8 rounded-xl border border-[#FF3D8B]/40 bg-gradient-to-r from-[#FF3D8B]/15 to-[#A855F7]/15 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
          data-testid="ad-offer-bar"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF3D8B] text-black flex items-center justify-center">
              <Tag size={18} />
            </div>
            <div>
              <div className="font-display text-lg text-white leading-none">
                {promoCode ? `${promoCode} — auto-applied at checkout` : "Free US shipping over $25"}
              </div>
              <div className="text-white/60 text-xs mt-1">
                Fast, discreet delivery to {cityLabel} · 3–5 business days
              </div>
            </div>
          </div>
          <Link
            to="/shop"
            data-testid="ad-offer-shop-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors"
          >
            Shop Now <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Hero */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 text-[#A855F7] uppercase text-xs tracking-widest mb-3">
          <MapPin size={12} /> {cityLabel}
        </div>
        <h1 className="font-display text-4xl sm:text-6xl text-white leading-[0.95]" data-testid="geo-h1">
          {data.keyword_phrase.split(" ").map((w, i) => (
            <span key={`kw-word-${w}-${i}`} className={i % 2 === 0 ? "text-white" : "text-neon-pink"}>{w}{" "}</span>
          ))}
          <span className="text-white/60 font-display">in</span>{" "}
          <span className="text-neon-purple">{data.city}</span>{data.state && <span className="text-white/60">, {data.state}</span>}
        </h1>
        <p className="mt-6 text-white/70 max-w-3xl leading-relaxed" data-testid="geo-intro">
          {data.intro}
        </p>
        <p className="mt-3 text-white/50 max-w-3xl text-sm leading-relaxed">{data.angle}</p>
      </div>

      {/* Stat cards — location-unique */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid="geo-stat-count">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Designs Shipping</div>
          <div className="font-display text-3xl text-white">{data.product_count}</div>
          <div className="text-white/40 text-xs mt-1">to {cityLabel}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid="geo-stat-price">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Price Range</div>
          <div className="font-display text-3xl text-white">${data.price_min.toFixed(2)}<span className="text-white/40 text-xl"> – ${data.price_max.toFixed(2)}</span></div>
          <div className="text-white/40 text-xs mt-1">per freshener</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid="geo-stat-themes">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Popular Notes</div>
          <div className="font-display text-lg text-white leading-snug">
            {(data.top_themes.slice(0, 3).join(" · ")) || "Peach · Vanilla · Cherry"}
          </div>
          <div className="text-white/40 text-xs mt-2">near {data.city}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Ships To</div>
          <div className="font-display text-3xl text-white">{data.state || "US"}</div>
          <div className="text-white/40 text-xs mt-1">discreet · 3–5 days</div>
        </div>
      </div>

      {/* Perks strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-y border-white/10 py-8 mb-12">
        {[
          { icon: Wind, title: "Long-lasting", sub: "3–4 weeks per hang" },
          { icon: Truck, title: "Discreet Shipping", sub: `Plain packaging to ${cityLabel}` },
          { icon: Sparkles, title: "Hand-poured", sub: "Small-batch premium oils" },
        ].map(({ icon: Icon, title, sub }) => (
          <div key={title} className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full border border-[#FF3D8B]/40 text-[#FF3D8B] flex items-center justify-center">
              <Icon size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-lg text-white leading-none">{title}</div>
              <div className="text-white/50 text-xs mt-1 uppercase tracking-wider">{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Listings */}
      <section className="mb-14">
        <h2 className="font-display text-3xl text-white mb-6" data-testid="geo-listings-heading">
          Shop {data.keyword_phrase} — shipping to {cityLabel}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.listings.slice(0, 9).map((p, idx) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.4 }}
            >
              <Link
                to={`/product/${p.id}`}
                data-testid={`geo-listing-${p.id}`}
                className="group block rounded-xl border border-white/10 bg-[#111] overflow-hidden hover:border-[#FF3D8B] hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(255,61,139,0.3)] transition-[transform,border-color,box-shadow] duration-300"
              >
                <div className="aspect-square bg-black overflow-hidden">
                  <img src={resolveImage(p.image_url)} alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-display text-lg text-white group-hover:text-[#FF3D8B] transition-colors">{p.name}</div>
                    <div className="text-white font-bold text-sm">${p.price.toFixed(2)}</div>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed">{p.tagline}</p>
                  {p.distance_miles != null && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-[#A855F7]">
                      {p.distance_miles} mi from {data.city}
                    </div>
                  )}
                </div>
              </Link>
              {adMode && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    addItem({ id: p.id, name: p.name, price: p.price, image_url: p.image_url }, 1);
                  }}
                  data-testid={`geo-quick-add-${p.id}`}
                  className="mt-2 w-full py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-[11px] hover:bg-[#ff5ba0] transition-colors"
                >
                  Add to Cart · ${p.price.toFixed(2)}
                </button>
              )}
            </motion.div>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to="/shop"
            data-testid="geo-shop-all-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors"
          >
            Browse All {data.total_count} Designs <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Siblings */}
      {data.siblings?.length > 0 && (
        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">
            {data.keyword_phrase.charAt(0).toUpperCase() + data.keyword_phrase.slice(1)} in nearby cities
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/${keyword}/${s.slug}`}
                data-testid={`geo-sibling-${s.slug}`}
                className="text-sm px-4 py-2 rounded-full border border-white/15 text-white/80 hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors"
              >
                {s.city}, {s.state}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="mb-16" data-testid="geo-faq-section">
        <h2 className="font-display text-3xl text-white mb-6">FAQ — {cityLabel}</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <details key={f.q || `faq-${i}`} className="group rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid={`geo-faq-${i}`}>
              <summary className="font-semibold text-white cursor-pointer flex justify-between items-center list-none">
                <span>{f.q}</span>
                <span className="text-[#FF3D8B] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-white/70 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="rounded-2xl border border-[#FF3D8B]/30 bg-gradient-to-br from-[#FF3D8B]/10 to-[#A855F7]/10 p-10 text-center">
        <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-2">Ready to upgrade your ride?</div>
        <h3 className="font-display text-3xl sm:text-4xl text-white leading-tight">
          Get {data.keyword_phrase} shipped to {cityLabel} — today.
        </h3>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors">
          Shop Booty Breeze <ArrowRight size={14} />
        </Link>
      </section>
      </div>
    </div>
  );
}
